import type {
  NarrativeSection as NarrativeSectionType,
  DataValues,
  NewNarrativeData,
  NarrativeMetadata,
} from "../types/narrative";
import { InsightGroup } from "./InsightGroup";

interface NarrativeSectionProps {
  section: NarrativeSectionType;
  data: DataValues;
  startGroupIndex: number; // Global group index where this section starts
  currentGroupIndex: number; // Currently visible group index
  onGroupAnimationComplete: (groupIndex: number) => void;
  onReactionSelect?: () => void;
  selectedSections?: Set<string>;
  onSectionToggle?: (sectionId: string, isSelected: boolean) => void;
  onContinue?: () => void;
  contentDelayed?: boolean; // When true, delay content animation (for interstitial)
  isNavigatingBackward?: boolean; // Whether user is navigating backward
  narrativeData?: NewNarrativeData; // Optional - pass down for highlights_gallery
  metadata?: NarrativeMetadata; // Optional - for curator resolution in contentAuthor
  format?: string | null;
  noAnimation?: boolean; // When true, disable all animations for instant rendering (PDF export)
  onPlayWithMusic?: () => void; // Called when user chooses to play with music
  onMuteMusic?: () => void; // Called when user chooses to continue without music
}

export function NarrativeSection({
  section,
  data,
  startGroupIndex,
  currentGroupIndex,
  onGroupAnimationComplete,
  onReactionSelect,
  selectedSections,
  onSectionToggle,
  onContinue,
  contentDelayed = false,
  isNavigatingBackward = false,
  narrativeData,
  metadata,
  format,
  noAnimation = false,
  onPlayWithMusic,
  onMuteMusic,
}: NarrativeSectionProps) {
  return (
    <>
      {section.groups.map((group, groupIndexInSection) => {
        const globalGroupIndex = startGroupIndex + groupIndexInSection;
        // Only the current group renders content - target group stays empty during scroll
        // Content will animate in after scroll completes and currentGroupIndex updates
        // Also respect contentDelayed for interstitial animation
        const isVisible =
          currentGroupIndex === globalGroupIndex && !contentDelayed;

        return (
          <div
            key={`${section.id}-${groupIndexInSection}`}
            className="h-full w-full snap-start snap-always flex-shrink-0"
          >
            <InsightGroup
              group={group}
              data={data}
              index={globalGroupIndex}
              isVisible={isVisible}
              onAnimationComplete={() =>
                onGroupAnimationComplete(globalGroupIndex)
              }
              onReactionSelect={onReactionSelect}
              sectionId={section.id}
              colorPalette={section.colorPalette}
              sectionAutoScroll={section.autoScroll}
              selectedSections={selectedSections}
              onSectionToggle={onSectionToggle}
              onContinue={onContinue}
              isNavigatingBackward={isNavigatingBackward}
              narrativeData={narrativeData}
              metadata={metadata}
              format={format}
              noAnimation={noAnimation}
              onPlayWithMusic={onPlayWithMusic}
              onMuteMusic={onMuteMusic}
            />
          </div>
        );
      })}
    </>
  );
}
